import { z } from 'zod';

/**
 * F26 — FCM Push + Unified In-App Inbox frozen API contracts.
 *
 * See plans/26_F26_fcm_push.md (Wave 1 / Task 1.3) for the founder-frozen
 * shape of every Zod schema and type alias below. Wave 2 sub-agents (2A
 * backend / 2B mobile / 2C portal / 2D admin / 2E marketing) develop against
 * these contracts in parallel — DO NOT rename, drop, or add fields without
 * escalating to the orchestrator first (contract-change protocol).
 *
 * F26 ships:
 *   - per-(user × device) FCM token registration (5-device LRU cap)
 *   - per-(user × category × channel) preference matrix (opt-out)
 *   - unified in-app inbox row + load-bearing idempotency record
 *   - per-(inbox × channel) delivery audit + retry-worker queue
 *
 * Three notification categories ship at MVP — `team`/`promotion`/`trainer`
 * are reserved placeholders (registered with `enabledAtMvp: false`; the
 * dispatcher accepts events of those categories silently until F30/F40/F33
 * ship).
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/**
 * Decision 6 — seven categories ship at MVP (4 enabled + 3 placeholders).
 * The dispatcher reads `category` from each event for preference lookup +
 * rate-limit bucketing. The frozen `kind → category` mapping lives in
 * `@bravemove/i18n/notification-categories.ts` (Task 1.4).
 */
export const NOTIFICATION_CATEGORY_VALUES = [
  'booking',
  'payment',
  'commission',
  'system',
  'team',
  'promotion',
  'trainer',
  // F32 — Open Games Marketplace (Decision D22). One preference toggle for the
  // 6 open_game.* events, cleanly separate from `booking`/`team`. The inbox +
  // preferences CHECK constraints are widened to admit this value in migration
  // 0027 (additive; the frozen 0019/0026 migrations are untouched).
  'open_game',
  // F36 — Trainer-Managed Teams (Club archetype) (D12). One preference toggle
  // for the 10 club.* events. The inbox + preferences CHECK constraints are
  // widened to admit this value in migration 0031 (additive).
  'club',
  // F37 — Player ↔ Trainer Recommendations (D14). One preference toggle for the
  // 6 user-facing recommendation.* events (review prompt/received/removed +
  // badge received/revoked). `badge_visibility_toggled` is audit/analytics only
  // (no notification). The inbox + preferences CHECK constraints are widened to
  // admit this value in migration 0032 (additive).
  'recommendation',
  // F38 — B2B SaaS Subscription Tiers (Decision 9 / §9). One preference toggle
  // for the 9 subscription.* events (trial started/ending/ended, invoice
  // issued/overdue, past_due, tier-switch-effective, admin-override-applied,
  // feature-gate-blocked). The inbox + preferences CHECK constraints are
  // widened to admit this value in migration 0033 (additive).
  'subscription',
  // F39 — Featured Placements (§10 / D-sponsored-notif-cap). The consumer-facing
  // ad-delivery category for the `sponsored_notification` placement TYPE — the
  // actual ad shown to a consumer. User-mutable INDEPENDENTLY of every other
  // category; in_app + push only (NEVER email); capped 2/user/week from
  // non-followed venues; never inserted into a transactional category. The inbox
  // + preferences CHECK constraints are widened to admit this value in migration
  // 0034 (additive).
  'sponsored',
  // F39 — Featured Placements (§10 / D-venue-notif-category). The venue-owner-facing
  // category for the 7 placement lifecycle events (purchased, approved, rejected,
  // starting_in_24h, active, expired, weekly_summary). Mirrors F38's `subscription`
  // precedent — gives the owner an independent mute axis. Distinct from the
  // consumer-facing `sponsored` category. The inbox + preferences CHECK
  // constraints are widened to admit this value in migration 0034 (additive).
  'placement',
  // F41 — Tournament & Event Listings (D8). One preference toggle for the 16
  // tournament.* events (lifecycle / registration / payment / capacity /
  // reminder / cancel / announcement / near-user / bracket / match / champion /
  // venue-hosting). `tournament_near_user` is in_app + push only (NEVER email)
  // at the fan-out layer; `tournament_capacity_reached`/`*_venue_host_*` target
  // the organizer/venue. Mirrors F36's `club` precedent. The inbox + preferences
  // CHECK constraints are widened to admit this value in migration 0036 (additive).
  'tournament',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORY_VALUES)[number];

/**
 * F28 D17 / FIX 3.1 — the email-only opt-out categories (migration 0020 admits
 * them on `user_notification_preferences`, never on the inbox). The matrix
 * carries one `email` cell for each, and the dispatcher gates their kinds on
 * that cell (`email.monthly_digest` → `monthly_digest`). The newsletter has no
 * producer yet; its cell is honoured once one exists.
 */
export const EMAIL_SUBSCRIPTION_CATEGORY_VALUES = ['monthly_digest', 'newsletter'] as const;
export type EmailSubscriptionCategory = (typeof EMAIL_SUBSCRIPTION_CATEGORY_VALUES)[number];

/** Every category a preference cell can carry. */
export const PREFERENCE_CATEGORY_VALUES = [
  ...NOTIFICATION_CATEGORY_VALUES,
  ...EMAIL_SUBSCRIPTION_CATEGORY_VALUES,
] as const;
export type PreferenceCategory = (typeof PREFERENCE_CATEGORY_VALUES)[number];

/**
 * Decision 3 — four channels at MVP. `in_app` writes to `in_app_notifications`
 * (always-on at MVP; preference can disable it post-MVP). `push` = FCM (mobile
 * only). `email` = F09 UserEmailService / F28 SES. `whatsapp` = F27 Business
 * API utility templates (stub at F26 — try/catch swallow per F09 pattern).
 */
export const NOTIFICATION_CHANNEL_VALUES = ['in_app', 'push', 'email', 'whatsapp'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNEL_VALUES)[number];

/**
 * Decision 12 — three inbox states. `unread → read → dismissed`. Retention
 * worker prunes `dismissed > 90 days` and `read > 365 days` (Wave 2A); unread
 * is kept indefinitely.
 */
export const IN_APP_NOTIFICATION_STATES = ['unread', 'read', 'dismissed'] as const;
export type InAppNotificationState = (typeof IN_APP_NOTIFICATION_STATES)[number];

/** Inbox filters are applied by the server before cursor pagination. */
export const ListInboxQuerySchema = z
  .object({
    cursor: z.string().min(1).max(2048).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    state: z
      .string()
      .transform((value) => value.split(','))
      .pipe(z.array(z.enum(IN_APP_NOTIFICATION_STATES)).min(1).max(3))
      .optional(),
    category: z.enum(NOTIFICATION_CATEGORY_VALUES).optional(),
    q: z.string().trim().max(200).optional(),
  })
  .strict();

/**
 * Decision 9 — per-attempt status enum. `delivered` is terminal success;
 * `pending_retry` is picked up by the retry worker on its next tick;
 * `permanent_failure` is terminal (3-strikes exhausted OR non-retryable FCM
 * error). The remaining three statuses cover the "didn't actually try" paths.
 */
export const NOTIFICATION_DELIVERY_STATUSES = [
  'delivered',
  'pending_retry',
  'permanent_failure',
  'suppressed_by_preference',
  'rate_limited',
  'no_active_tokens',
] as const;
export type NotificationDeliveryStatus = (typeof NOTIFICATION_DELIVERY_STATUSES)[number];

/**
 * Decision 5 — mobile platforms supported at MVP. Web push (`'web'`) is
 * explicitly out of scope; the union widens additively when web push lands.
 */
export const DEVICE_TOKEN_PLATFORMS = ['ios', 'android'] as const;
export type DeviceTokenPlatform = (typeof DEVICE_TOKEN_PLATFORMS)[number];

/**
 * Device-token lifecycle. `active → revoked` (terminal soft-delete).
 */
export const DEVICE_TOKEN_STATUSES = ['active', 'revoked'] as const;
export type DeviceTokenStatus = (typeof DEVICE_TOKEN_STATUSES)[number];

/**
 * Decision 5 — max active device tokens per user. The 6th registration
 * evicts the LRU active row by `last_active_at`. Enforced service-side.
 */
export const MAX_DEVICE_TOKENS_PER_USER = 5 as const;

/**
 * Decision 10 — rate-limit cap per (user × category) per hour. Excess is
 * silently dropped (pino warn + inbox row NOT written). Defensive bound;
 * real expected volume per category is well under 5/hr.
 */
export const NOTIFICATION_RATE_LIMIT_PER_HOUR = 20 as const;

/**
 * Decision 9 — FCM retry backoff schedule (seconds from first failed
 * attempt). Three attempts then `permanent_failure`. Worker polls every 60s.
 */
export const NOTIFICATION_RETRY_BACKOFF_SECONDS = [30, 300, 3600] as const;

/**
 * Decision 12 — retention worker prune thresholds (days).
 */
export const NOTIFICATION_DISMISSED_RETENTION_DAYS = 90 as const;
export const NOTIFICATION_READ_RETENTION_DAYS = 365 as const;

// ─── Entity shapes (read-side projections) ───────────────────────────────────

/**
 * One row of the `device_tokens` table (Decision 5). The `fcmToken` field is
 * the opaque FCM identifier returned by `messaging().getToken()` on mobile;
 * the dispatcher uses it as the FCM SDK's `to` parameter. `revokedAt` +
 * `revokedReason` populated when status flips to 'revoked' (sign-out,
 * UNREGISTERED FCM response, or LRU eviction by the 6th-device cap).
 */
export type DeviceToken = {
  readonly id: string;
  readonly userId: string;
  readonly fcmToken: string;
  readonly platform: DeviceTokenPlatform;
  readonly deviceName: string | null;
  readonly appVersion: string | null;
  readonly osVersion: string | null;
  readonly status: DeviceTokenStatus;
  readonly registeredAt: string;
  readonly lastActiveAt: string;
  readonly revokedAt: string | null;
  readonly revokedReason: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * One cell of the per-(user × category × channel) preference matrix
 * (Decision 3). At MVP `quietHours*` columns are RESERVED nullable for
 * future use; F26 ships no UI for them (Decision 13).
 */
export type NotificationPreferenceCell = {
  readonly userId: string;
  readonly category: PreferenceCategory;
  readonly channel: NotificationChannel;
  readonly enabled: boolean;
  readonly quietHoursStartMinute: number | null;
  readonly quietHoursEndMinute: number | null;
  readonly quietHoursTimezone: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * Full per-user preference matrix. Returned by `GET /me/notifications/
 * preferences` as a flat list of cells; the UI renders it as a grid. The API
 * always returns the full matrix for ENABLED-at-MVP categories (4 × 4 = 16
 * cells); placeholder categories (team/promotion/trainer) only return cells
 * if rows exist (won't until F30/F40/F33 ship).
 */
export type NotificationPreferenceMatrix = {
  readonly userId: string;
  readonly cells: readonly NotificationPreferenceCell[];
};

/**
 * One row of the `in_app_notifications` table. The load-bearing idempotency
 * record AND the read surface for the inbox UI. `sourceEventId` is the
 * UUIDv7 generated at the call site (e.g. inside `BookingService.acceptBooking`)
 * — same source event id → same idempotency.
 *
 * `actions` carries the SCHEMA'D action-button list (Decision 11); empty array
 * = no action buttons (most events at MVP). Mobile renders tap-to-deep-link
 * only at F26; the OS-side action-button UI ships in a follow-up wave once
 * iOS-Mac unblocks.
 */
export type InAppNotificationAction = {
  readonly id: string;
  readonly label: string;
  readonly deepLink: string;
};

export type InAppNotification = {
  readonly id: string;
  readonly sourceEventId: string;
  readonly recipientUserId: string;
  readonly category: NotificationCategory;
  readonly kind: string;
  readonly title: string;
  readonly body: string;
  readonly deepLink: string;
  readonly actions: readonly InAppNotificationAction[];
  readonly payload: Record<string, unknown>;
  readonly state: InAppNotificationState;
  readonly readAt: string | null;
  readonly dismissedAt: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
};

/**
 * One row of the `notification_delivery_attempts` table. One row per
 * (inbox × channel) — UPDATE on retry (single-row per pair). `attemptCount`
 * starts at 1 on first attempt; `nextAttemptAt` is set per the
 * `NOTIFICATION_RETRY_BACKOFF_SECONDS` schedule on transient failure.
 */
export type NotificationDeliveryAttempt = {
  readonly id: string;
  readonly inboxNotificationId: string;
  readonly channel: NotificationChannel;
  readonly status: NotificationDeliveryStatus;
  readonly attemptCount: number;
  readonly lastAttemptAt: string | null;
  readonly nextAttemptAt: string | null;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
  readonly fcmMessageId: string | null;
  /**
   * F28 — Postmark MessageID for `channel='email'` rows. Null on push/in_app/whatsapp.
   * Webhook handlers correlate bounce + complaint payloads back to the per-attempt
   * row by this id (the `notification_delivery_attempts_postmark_msg_id_idx`
   * partial index lives in migration 0020).
   */
  readonly postmarkMessageId: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/**
 * Decision 14 — dry-run mode return shape. Admin-only endpoint
 * `POST /admin/notifications/dry-run { event }` returns what WOULD be sent
 * without actually writing inbox rows or calling external channels. Useful
 * for QA + adding new event types in future features.
 */
export type DryRunResult = {
  readonly sourceEventId: string;
  readonly category: NotificationCategory;
  readonly kind: string;
  /**
   * `userId` is `null` when the event kind has no resolvable recipient
   * (admin-internal alerts like `venueClaim.submitted`). The dry-run still
   * short-circuits with zero side effects and every channel decision reads
   * `'no_recipient'`.
   */
  readonly recipient: {
    readonly userId: string | null;
    readonly email: string | null;
    readonly displayName: string | null;
  };
  readonly channelDecisions: {
    readonly in_app: 'would_send' | 'suppressed_by_preference' | 'skipped' | 'no_recipient';
    readonly push:
      | 'would_send'
      | 'suppressed_by_preference'
      | 'no_active_tokens'
      | 'skipped'
      | 'no_recipient';
    readonly email: 'would_send' | 'suppressed_by_preference' | 'skipped' | 'no_recipient';
    readonly whatsapp: 'would_send' | 'suppressed_by_preference' | 'skipped' | 'no_recipient';
  };
  readonly activeDeviceTokenCount: number;
};

/**
 * Dispatcher return shape (Wave 2A). Returned to callers from
 * `NotificationOrchestrator.dispatch(event)`. `isReplay: true` indicates the
 * source event was already delivered to this recipient (23505 caught at
 * inbox-write — Decision 8); the rest of the fan-out short-circuits.
 */
export type DispatchResult = {
  readonly sourceEventId: string;
  readonly inboxNotificationId: string | null;
  readonly isReplay: boolean;
  readonly channelOutcomes: {
    readonly in_app: 'delivered' | 'replay' | 'failed';
    readonly push:
      | 'delivered'
      | 'pending_retry'
      | 'permanent_failure'
      | 'suppressed_by_preference'
      | 'no_active_tokens'
      | 'rate_limited'
      | 'skipped';
    /**
     * F28 — the email channel outcome carries the full F26 `NotificationDeliveryStatus`
     * union when an EmailChannel adapter is plumbed into the orchestrator. The
     * legacy `'queued'` value is preserved for the pre-F28 boot path (and tests
     * that omit `emailChannel` from the F26 dependency bundle).
     */
    readonly email:
      | 'delivered'
      | 'pending_retry'
      | 'permanent_failure'
      | 'suppressed_by_preference'
      | 'no_active_tokens'
      | 'rate_limited'
      | 'queued'
      | 'skipped';
    readonly whatsapp: 'queued' | 'skipped' | 'suppressed_by_preference';
  };
};

// ─── Zod input schemas ───────────────────────────────────────────────────────

/**
 * `POST /me/device-tokens` body — register or refresh a device's FCM token.
 * Service layer additionally:
 *   - Catches the partial-unique-on-fcm-token race → revokes the prior row
 *     (account switch on the same device) + inserts a new one for the new
 *     `user_id`.
 *   - Enforces the 5-device LRU cap (Decision 5); evicts the oldest active
 *     row by `last_active_at` if the user is already at 5.
 *
 * `deviceName` / `appVersion` / `osVersion` are best-effort — mobile sends
 * what it can read from `react-native-device-info`-equivalent; absent OK.
 */
export const RegisterDeviceTokenInputSchema = z
  .object({
    fcmToken: z.string().trim().min(1).max(4096),
    platform: z.enum(DEVICE_TOKEN_PLATFORMS),
    deviceName: z.string().trim().max(120).optional(),
    appVersion: z.string().trim().max(40).optional(),
    osVersion: z.string().trim().max(40).optional(),
  })
  .strict();
export type RegisterDeviceTokenInput = z.infer<typeof RegisterDeviceTokenInputSchema>;

/**
 * FIX 5.14 (§4.4-F14) — preference cells a user can't turn off. The whole
 * `system` row carries the unopt-outable admin→owner alerts (F42 D10), and
 * booking/payment × email carry the required transactional emails (F28). The
 * API refuses `enabled: false` on these (422 `PREFERENCE_LOCKED`); clients
 * render them read-only and forced on.
 */
export const LOCKED_PREFERENCE_CATEGORIES: readonly NotificationCategory[] = ['system'];
export const LOCKED_EMAIL_PREFERENCE_CATEGORIES: readonly NotificationCategory[] = [
  'booking',
  'payment',
  'system',
];
export function isPreferenceCellLocked(
  category: PreferenceCategory,
  channel: NotificationChannel,
): boolean {
  const c = category as NotificationCategory;
  return (
    LOCKED_PREFERENCE_CATEGORIES.includes(c) ||
    (channel === 'email' && LOCKED_EMAIL_PREFERENCE_CATEGORIES.includes(c))
  );
}

/**
 * `PATCH /me/notifications/preferences` body — toggle one or more cells
 * atomically. Cells reference existing categories + channels; an
 * unrecognised value returns 422 INVALID_CATEGORY / INVALID_CHANNEL.
 */
export const UpdateNotificationPreferenceInputSchema = z
  .object({
    cells: z
      .array(
        z
          .object({
            category: z.enum(PREFERENCE_CATEGORY_VALUES),
            channel: z.enum(NOTIFICATION_CHANNEL_VALUES),
            enabled: z.boolean(),
          })
          .strict(),
      )
      .min(1)
      .max(PREFERENCE_CATEGORY_VALUES.length * NOTIFICATION_CHANNEL_VALUES.length),
  })
  .strict();
export type UpdateNotificationPreferenceInput = z.infer<
  typeof UpdateNotificationPreferenceInputSchema
>;

/**
 * `POST /me/notifications/:id/read` — no body, just the path param. Schema
 * exists for symmetry with the other endpoints; route handler validates
 * `id` as a UUID via Fastify schema.
 */
export const MarkReadInputSchema = z.object({}).strict();
export type MarkReadInput = z.infer<typeof MarkReadInputSchema>;

/**
 * `POST /admin/notifications/dry-run` body — paste a JSON event to see who
 * would receive it + how. The route handler validates `event` as a stringly-
 * typed kind + payload; full type-safety is deferred to runtime because the
 * dispatcher's NotificationEvent union lives in apps/api (not shared via
 * @bravemove/types per Decision F1 / verification finding).
 *
 * Decision 14 — super_admin only via the `notifications:dry_run` permission.
 */
export const DryRunInputSchema = z
  .object({
    kind: z.string().trim().min(1).max(120),
    recipientUserId: z.string().uuid(),
    // Free-form payload; the dispatcher does its own kind-specific shape check.
    payload: z.record(z.string(), z.unknown()).default({}),
  })
  .strict();
export type DryRunInput = z.infer<typeof DryRunInputSchema>;

/**
 * `GET /admin/notifications/test-send` body — sends a test event to the
 * admin's own user_id (the admin must also have a `users` row; if not, the
 * route gracefully fails with `TEST_SEND_NO_USER_ACCOUNT`). Useful for
 * verifying FCM creds end-to-end after AWS setup.
 */
export const TestSendInputSchema = z
  .object({
    category: z.enum(NOTIFICATION_CATEGORY_VALUES).default('system'),
    kind: z.string().trim().min(1).max(120).default('system.test_send'),
    title: z.string().trim().min(1).max(200).default('BraveMove test notification'),
    body: z
      .string()
      .trim()
      .min(1)
      .max(500)
      .default('This is a test from /admin/notifications/test-send.'),
    deepLink: z.string().trim().min(1).max(2048).default('https://app.bravemove.app/notifications'),
  })
  .strict();
export type TestSendInput = z.infer<typeof TestSendInputSchema>;

// ─── Error codes ─────────────────────────────────────────────────────────────

/**
 * F26 error vocabulary. Every API error response returns one of these as
 * `error.code`. Service layer maps to HTTP status codes:
 *
 *   - `INVALID_*` / `*_OUT_OF_BOUNDS` → 422
 *   - `*_NOT_FOUND` → 404
 *   - `*_DUPLICATE` → 409 (idempotent replay; consumer treats as no-op)
 *   - `*_LIMIT_EXCEEDED` → 422 (with current/limit in payload)
 *   - `RATE_LIMITED` → 429
 *   - `FCM_*` → 502 (upstream error from Firebase)
 *   - `TEST_SEND_NO_USER_ACCOUNT` → 422 (admin must have a `users` row)
 */
export const NOTIFICATION_ERROR_CODES = [
  'NOTIFICATION_NOT_FOUND',
  'DEVICE_TOKEN_NOT_FOUND',
  'DEVICE_TOKEN_DUPLICATE',
  'DEVICE_LIMIT_EXCEEDED',
  'INVALID_CATEGORY',
  'INVALID_CHANNEL',
  'PREFERENCE_LOCKED',
  'RATE_LIMITED',
  'IDEMPOTENT_REPLAY',
  'FCM_PERMANENT_FAILURE',
  'DRY_RUN_INVALID_EVENT',
  'TEST_SEND_NO_USER_ACCOUNT',
] as const;
export type NotificationErrorCode = (typeof NOTIFICATION_ERROR_CODES)[number];
